import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { SlackSourceSettingsApi } from "./SlackSourceSettings.Api";
import type { SlackSourceSettingsViewModel } from "./SlackSourceSettings.ViewModel.types";
import type {
  SlackChannel,
  SlackConfiguration,
  SlackImportMode,
} from "./SlackSourceSettings.types";

/** The emoji name as Slack reports it: no colons, no surrounding space, lower case. */
const normaliseReactionName = (name: string): string =>
  name.trim().replace(/^:+|:+$/g, "").trim().toLowerCase();

export const useSlackSourceSettingsViewModel = (
  model: SourceSettingsModel,
): SlackSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );

  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [channelsLoaded, setChannelsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [recentReactions, setRecentReactions] = useState<string[]>([]);
  const [reactionsLoaded, setReactionsLoaded] = useState(false);
  const [reactionQuery, setReactionQuery] = useState("");
  const [listReference, setListReference] = useState("");
  const [isResolvingList, setIsResolvingList] = useState(false);
  const [hasListError, setHasListError] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.Slack && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<SlackConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  const getSlackApi = (): SlackSourceSettingsApi | null =>
    sourceSettingsApiRef.current?.tryGetSourceApi<SlackSourceSettingsApi>(
      SourceTypeIds.Slack,
    ) ?? null;

  const update = (updater: (cfg: Partial<SlackConfiguration>) => Partial<SlackConfiguration>): void => {
    updateConnectionConfiguration<SlackConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      updater,
    );
  };

  // Fill in defaults once per connection. A connection saved for starred items has no mode, so
  // it opens in reaction mode with its channels and file setting kept. No reaction is chosen for
  // the user: the connection can't be saved until they pick one.
  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    updateConnectionConfiguration<SlackConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        mode: cfg.mode ?? "reactions",
        reactionNames: cfg.reactionNames ?? [],
        includeFiles: cfg.includeFiles ?? true,
        channels: cfg.channels ?? [],
        lists: cfg.lists ?? [],
        onlyAssignedToMe: cfg.onlyAssignedToMe ?? false,
      }),
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible]);

  // A different account may not see the same channels or Lists, so clear them.
  useEffect(() => {
    const currentProfileIdentity = `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setChannels([]);

    updateConnectionConfiguration<SlackConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        channels: [],
        lists: [],
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  useEffect(() => {
    const loadChannels = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId) {
        setChannels([]);
        setChannelsLoaded(true);
        return;
      }

      const slackApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<SlackSourceSettingsApi>(
          SourceTypeIds.Slack,
        );

      if (!slackApi) {
        setChannels([]);
        setChannelsLoaded(true);
        return;
      }

      setChannelsLoaded(false);
      setHasError(false);
      try {
        setChannels(await slackApi.getChannels(model.boardId, editingConnectionId));
      } catch {
        setChannels([]);
        setHasError(true);
      } finally {
        setChannelsLoaded(true);
      }
    };

    void loadChannels();
  }, [editingConnectionId, isVisible, model.boardId]);

  // Recent reactions are only suggestions; any name can be typed, so a failure leaves none.
  useEffect(() => {
    const loadRecentReactions = async (): Promise<void> => {
      const slackApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<SlackSourceSettingsApi>(
          SourceTypeIds.Slack,
        );

      if (!isVisible || !model.boardId || !editingConnectionId || !slackApi) {
        setRecentReactions([]);
        setReactionsLoaded(true);
        return;
      }

      setReactionsLoaded(false);
      try {
        setRecentReactions(
          await slackApi.getRecentReactions(model.boardId, editingConnectionId),
        );
      } catch {
        setRecentReactions([]);
      } finally {
        setReactionsLoaded(true);
      }
    };

    void loadRecentReactions();
  }, [editingConnectionId, isVisible, model.boardId]);

  const mode: SlackImportMode = configuration.mode === "lists" ? "lists" : "reactions";
  const lists = configuration.lists ?? [];
  const trimmedListReference = listReference.trim();
  const reactionNames = configuration.reactionNames ?? [];
  const typedReactionName = normaliseReactionName(reactionQuery);
  const reactionOptions = Array.from(
    new Set([
      ...(typedReactionName ? [typedReactionName] : []),
      ...reactionNames,
      ...recentReactions,
    ]),
  );

  return {
    isVisible,
    mode,
    isReactionMode: mode === "reactions",
    isListMode: mode === "lists",

    reactionNames,
    reactionOptions,
    reactionQuery,
    reactionsLoaded,
    hasNoReactions: mode === "reactions" && reactionNames.length === 0,
    includeFiles: configuration.includeFiles ?? true,
    channels,
    channelsLoaded,
    hasError,
    selectedChannelIds: (configuration.channels ?? []).map((channel) => channel.id),

    lists,
    listReference,
    canAddList: trimmedListReference.length > 0 && !isResolvingList,
    isResolvingList,
    hasListError,
    onlyAssignedToMe: configuration.onlyAssignedToMe ?? false,

    onSelectMode: (nextMode: SlackImportMode) => {
      update((cfg) => ({ ...cfg, mode: nextMode }));
    },

    onReactionQueryChange: (query: string) => {
      setReactionQuery(query);
    },

    onSelectReactions: (names: string[]) => {
      const chosen = Array.from(
        new Set(names.map(normaliseReactionName).filter((name) => name.length > 0)),
      );
      update((cfg) => ({ ...cfg, reactionNames: chosen }));
      setReactionQuery("");
    },

    onToggleIncludeFiles: (isEnabled: boolean) => {
      update((cfg) => ({ ...cfg, includeFiles: isEnabled }));
    },

    onSelectChannels: (ids: string[]) => {
      const selectedChannels = channels.filter((channel) => ids.includes(channel.id));
      update((cfg) => ({ ...cfg, channels: selectedChannels }));
    },

    onChangeListReference: (reference: string) => {
      setListReference(reference);
      setHasListError(false);
    },

    onAddList: async () => {
      const slackApi = getSlackApi();

      if (!trimmedListReference || !model.boardId || !slackApi) {
        return;
      }

      setIsResolvingList(true);
      setHasListError(false);
      try {
        const resolved = await slackApi.resolveList(
          model.boardId,
          editingConnectionId,
          trimmedListReference,
        );
        update((cfg) => ({
          ...cfg,
          lists: [...(cfg.lists ?? []).filter((list) => list.id !== resolved.id), resolved],
        }));
        setListReference("");
      } catch {
        setHasListError(true);
      } finally {
        setIsResolvingList(false);
      }
    },

    onRemoveList: (id: string) => {
      update((cfg) => ({
        ...cfg,
        lists: (cfg.lists ?? []).filter((list) => list.id !== id),
      }));
    },

    onToggleOnlyAssignedToMe: (isEnabled: boolean) => {
      update((cfg) => ({ ...cfg, onlyAssignedToMe: isEnabled }));
    },
  };
};
